import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Camera, Eye, X, UploadCloudIcon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { RootState } from '@/store';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Court, Sports } from '@/types/interfaceType';
import { Loader2, Loader } from "lucide-react";
import { toast as newToast } from "@/components/ui/sonner";
import routes from '@/routes/routes.json';

import { useNavigate, useLocation } from 'react-router-dom';

import { getVendorVenueDetailsById, updateturfImages, deleteImages } from '@/store/thunkMidddleware/turfMiddleware';
import Strings from '@/constants/String';
import { ButtonWithLoader } from '../ui/buttonwithloader';
import OnBoardingSuccessAnimation from '../animation/OnBoardingAnimation';
import VendorAgreementModal from './VendorAgreementModal';
// import { addSport, addCourt, updateSport, updateCourt, deleteCourt, deleteSport, setEditSportAndCourtDetails, updateEditCourtDetails, updateEditSportDetails, setAddNewCourtState, resetAddNewCourtState } from '@/store/slices/turfSlice';

const SPORT_TYPES = [
    'Football', 'Cricket', 'Basketball', 'Tennis', 'Badminton', 'Volleyball',
    'Table Tennis', 'Swimming', 'Boxing', 'Hockey', 'Squash', 'Golf'
];

// The fewest photos a court can have before the venue counts as set up.
const MIN_COURT_IMAGES = 3;

// Mirrors the backend's getOnboardingPercentage (GroundModalNew.js), which
// checks truthiness — so a price of 0 counts as missing there too. Returns the
// first thing it would reject, or null if it should report 100.
const missingOnboardingDetail = (venue: any): string | null => {
    if (!venue?.name) return "Venue name is missing."
    if (!venue?.baseBookingPricePerHour) return "Base price per hour is missing (it can't be 0)."
    if (!venue?.amenities) return "Amenities are missing."

    const address = venue?.address || {}
    const addressFields = ["address", "locality", "city", "state", "pincode"]
    const missingAddress = addressFields.find((field) => !address[field])
    if (missingAddress) return `Address ${missingAddress} is missing.`

    if (!venue?.sportsCategories) return "Sports categories are missing."
    if (!venue?.sports?.length) return "Add at least one sport."
    return null
};

interface SportsImageManagementFormProps {
    onNext: () => void;
    onBack: () => void;
}

interface ImageType {
    "url": string,
    "fileId": string,
    "bucketId": string,
    "index"?: number,
    "fileName": string,
    "createdAt"?: string,
    "updatedAt"?: string,
    "_id"?: string,
}


const SportsImageManagementForm: React.FC<SportsImageManagementFormProps> = ({ onNext, onBack }) => {
    const dispatch = useAppDispatch();
    const { sports, currentOnboardVenueDetails, VendorVenueDetailsById, updateturfImagesResponse, deleteImagesResponse } = useAppSelector((state: RootState) => state.turf);
    const { toast } = useToast();
    const navigate = useNavigate()

    const [selectedSportId, setSelectedSportId] = useState<string>('');
    const [selectedCourtId, setSelectedCourtId] = useState<string>('');
    const [allCourtData, setAllCourtData] = useState<Court[]>([])
    const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
    // const [venueImages, setVenueImages] = useState<(string | null)[]>(new Array(4).fill(null));
    const [venueImages, setVenueImages] = useState<(ImageType | null)[]>(new Array(4).fill(null));

    const [viewingImage, setViewingImage] = useState<string | null>(null); // To view image
    const [isUploadingThumnail, setIsUploadingThumnail] = useState(false)
    const [currentCourtImageIndex, setCurrentCourtImageIndex] = useState(null)
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [showAgreementModal, setShowAgreementModal] = useState(false)

    // Handle Thumbnail Image Upload
    const handleThumbnailUpload = (event: React.ChangeEvent<HTMLInputElement>) => {

        if (!selectedSportId) {
            newToast.error("Pelase select the sport.", { closeButton: true })
        }


        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setThumbnailPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
            setIsUploadingThumnail(true)
            UploadImages({
                imageType: Strings.ImgType.venuethumbnail,
                venueId: VendorVenueDetailsById.payload?._id,
                courtId: null, sportId: null, file: file, cb: null
            })
        }
    };

    useEffect(() => {
        if (VendorVenueDetailsById?.payload?.venueThumbnail) {
            setThumbnailPreview(VendorVenueDetailsById?.payload.venueThumbnail)

        }
    }, [VendorVenueDetailsById?.payload])

    // useEffect(() => {
    //     if (VendorVenueDetailsById?.payload && selectedSportId) {
    //         let reqSport = VendorVenueDetailsById?.payload?.sports?.find(ele => ele._id === selectedSportId)

    //         if (selectedCourtId && reqSport) {
    //             let reqCourtImages = reqSport?.courts?.find(ele => ele._id === selectedCourtId)?.images
    //             console.log(reqCourtImages)
    //             if (reqCourtImages?.length > 0) {
    //                 reqCourtImages?.map((ele) => {
    //                     setVenueImages((prevImages) => {
    //                         let updatedImages = [...prevImages];
    //                         updatedImages[ele?.index].url = ele?.url || ""
    //                         return updatedImages;
    //                     });
    //                 })

    //             } else {

    //                 setVenueImages(new Array(4).fill(null))
    //             }
    //         }

    //     }
    // }, [selectedSportId, VendorVenueDetailsById?.payload, selectedCourtId])

    useEffect(() => {
        if (VendorVenueDetailsById?.payload && selectedSportId && selectedCourtId) {
            const reqSport = VendorVenueDetailsById.payload.sports?.find(sport => sport._id === selectedSportId);
            if (reqSport) {
                const reqCourt = reqSport.courts?.find(court => court._id === selectedCourtId);
                const reqCourtImages = reqCourt?.images;

                if (reqCourtImages && reqCourtImages.length > 0) {
                    const updatedImages: (ImageType | null)[] = new Array(4).fill(null);

                    reqCourtImages.forEach((image) => {
                        if (typeof image.index === 'number' && image.index >= 0 && image.index < 4) {
                            updatedImages[image.index] = {
                                url: image.url || "",
                                index: image.index,
                                fileName: image.fileName,
                                bucketId: image.bucketId,
                                fileId: image.fileId,
                                createdAt: image.createdAt,
                                updatedAt: image.updatedAt,
                                _id: image._id
                            };
                        }
                    });

                    setVenueImages(updatedImages);
                } else {
                    setVenueImages(new Array(4).fill(null));
                }
            }
        }
    }, [selectedSportId, VendorVenueDetailsById?.payload, selectedCourtId]);


    const deleteVenueAndcourtImages = ({ fileId, fileName = null, imageType = null, venueId = null, courtId = null, sportId = null, imageId = null, cb = null, }) => {
        if (fileId && fileName) {
            dispatch(deleteImages({
                payload: { fileName, imageId, imageType, venueId, courtId, sportId, fileId }, callback: (res) => {
                    console.log("delete response:", res)
                    if (typeof cb === "function") {
                        cb(res)
                    }
                }
            }))
        } else {
            newToast.error("FileId, FileName is missing", { closeButton: true })
        }

    }
    const UploadImages = ({ imageType, venueId, courtId, sportId, file, cb = null, index = null }) => {
        let formdata = new FormData()
        formdata.append("imageType", imageType)
        formdata.append("venueId", venueId)
        formdata.append("courtId", courtId)
        formdata.append("sportId", sportId)
        formdata.append("image", file)
        if (index >= 0) {
            formdata.append("index", index)
        }
        dispatch(updateturfImages({
            payload: formdata, callback: (res) => {
                if (res?.payload?.uploadedData) {
                    if (imageType === Strings.ImgType.venuethumbnail) {
                        setThumbnailPreview(res?.payload?.uploadedData?.url)
                        setIsUploadingThumnail(false)
                    }
                    setCurrentCourtImageIndex(null);
                    if (typeof cb === "function") {
                        cb(res?.payload?.uploadedData)
                    }
                }

            }
        }))
    }

    // Handle Venue Image Upload
    const handleVenueImageUpload = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        setCurrentCourtImageIndex(index);
        if (!selectedSportId || !selectedCourtId) {
            newToast.error("Pelase select the court/Sport .", { closeButton: true })
        }

        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setVenueImages((prevImages) => {
                    const updatedImages = [...prevImages];

                    // Ensure a valid object exists at this index
                    updatedImages[index] = {
                        ...(updatedImages[index] || {}), // preserve existing props if any
                        url: reader.result as string,
                        index: index,
                        bucketId: new Date().getTime().toString(),
                        fileId: new Date().getTime().toString(),
                        fileName: file?.name || ""
                    };

                    return updatedImages;
                });
            };

            reader.readAsDataURL(file);

            if (selectedCourtId && selectedSportId) {
                UploadImages({
                    imageType: Strings.ImgType.courtsImages,
                    venueId: VendorVenueDetailsById.payload?._id,
                    courtId: selectedCourtId,
                    sportId: selectedSportId,
                    file: file,
                    index: index,
                    cb: (uploadedData: ImageType[]) => {
                        if (uploadedData.length > 0 && uploadedData[0]?.fileId) {
                            let newuploadedData = uploadedData[0]
                            setVenueImages((prevImages) => {
                                const updatedImages = [...prevImages];

                                // Ensure a valid object exists at this index
                                updatedImages[index] = {
                                    ...(updatedImages[index] || {}), // preserve existing props if any
                                    ...newuploadedData
                                    // url: uploadedData.url,
                                    // index: index,
                                    // bucketId:uploadedData.bucketId,
                                    // fileId: uploadedData.fileId,
                                    // fileName: file?.name || ""
                                };

                                return updatedImages;
                            });
                        }

                    }
                })
            } else {
                newToast.error("Image upload failed, check sport or court is selected", { closeButton: true })
            }
        }



    };

    // Handle Deleting Images
    const handleDeleteImage = (index: number) => {

        if (index === -1) {
            setThumbnailPreview(null);
        } else {
            setVenueImages((prevImages) => {
                const updatedImages = [...prevImages];
                updatedImages[index] = null;
                return updatedImages;
            });
            // setVenueImages((prevImages) => {
            //     const updatedImages = [...prevImages];
            //     updatedImages.splice(index, 1); // remove from array
            //     return updatedImages;
            // });
        }

    };

    const handleDeleteCourtImage = (index: number, imageDetails: ImageType) => {
        let fileName = imageDetails?.fileName
        if (!fileName && imageDetails.url) {
            let splitarray = imageDetails.url?.split("/")
            fileName = splitarray[splitarray.length - 1]
        }

        if (fileName && imageDetails?.fileId) {
            setCurrentCourtImageIndex(index);
            deleteVenueAndcourtImages({
                fileId: imageDetails?.fileId,
                venueId: VendorVenueDetailsById?.payload?._id, sportId: selectedSportId, courtId: selectedCourtId, fileName, imageId: imageDetails._id, imageType: Strings.ImgType.courtsImages, cb: (res) => {
                    if (res) {
                        setVenueImages((prevImages) => {
                            const updatedImages = [...prevImages];
                            updatedImages[index] = null;
                            return updatedImages;
                        });
                    }
                }
            })
        } else {
            newToast.error("File name is missing", { closeButton: true })
        }




    };


    // Handle Image View
    const handleViewImage = (image: string) => {
        setViewingImage(image);
    };

    // Returns false, and names the offending court, as soon as one court is
    // short on photos.
    const checkImages = (sportsArray: Sports[]) => {
        for (const sport of sportsArray || []) {
            for (const court of sport?.courts || []) {
                if ((court.images?.length || 0) < MIN_COURT_IMAGES) {
                    newToast.error(
                        `${sport.name} – ${court.courtName} needs at least ${MIN_COURT_IMAGES} images. Please upload them.`,
                        { closeButton: true }
                    )
                    return false
                }
            }
        }
        return true
    };


    // Handle Submit
    const handleSubmit = () => {
        fetchVendorVenueDetailsById((res) => {
            if (!res?.payload) {
                newToast.error("Could not check the venue setup, please try again", { closeButton: true })
                return
            }

            // A missing percentage counts as unfinished, so a partial response
            // can't wave the vendor through to payment.
            if ((res.payload.venueOnBoardingPercentage ?? 0) < 100) {
                if (!res.payload.venueThumbnail) {
                    newToast.error("Please upload venue thumbnail", { closeButton: true })
                    return
                }
                // checkImages names the court that is short on photos. Anything
                // else holding the percentage back lives on an earlier step, so
                // name that instead of a generic "not complete".
                if (checkImages(res.payload.sports || [])) {
                    const missing = missingOnboardingDetail(res.payload)
                    newToast.error(
                        missing
                            ? `${missing} Please go back and complete it.`
                            : "Venue setup is not complete yet, please review the earlier steps",
                        { closeButton: true }
                    )
                }
                return
            }

            if (!res.payload.oneTimeOnboardingFeeDetails?.isPaymentdone) {
                // The fee can only be paid against a signed agreement, so get
                // it signed here before asking for payment.
                if (!res.payload.isAgreementSigned) {
                    setShowAgreementModal(true)
                } else {
                    setShowSuccessModal(true)
                }
            } else {
                onCloseHandler()
            }
        })
    };

    const fetchVendorVenueDetailsById = (cb?: (res: any) => void) => {
        if (!currentOnboardVenueDetails?.payload?._id) {
            if (typeof cb === "function") {
                cb(null)
            }
            return
        }
        dispatch(getVendorVenueDetailsById({
            pathparams: currentOnboardVenueDetails?.payload?._id, callback: (res) => {
                if (typeof cb === "function") {
                    cb(res)
                }
            }
        }))
    }

    const onCloseHandler = () => {
        setShowSuccessModal(false)
        navigate(routes.venueList)
    }
    return (
        <>
            <OnBoardingSuccessAnimation
                onClose={onCloseHandler}
                show={showSuccessModal}

            />
            <VendorAgreementModal
                open={showAgreementModal}
                onOpenChange={setShowAgreementModal}
                venueId={currentOnboardVenueDetails?.payload?._id ?? null}
                onSigned={() => {
                    fetchVendorVenueDetailsById()
                    setShowSuccessModal(true)
                }}
            />
            <div className="flex items-start justify-center">

                <Card className="w-full max-w-2xl">

                    <CardHeader>
                        <CardTitle className="text-2xl font-bold">Venue Image Management</CardTitle>
                        <CardDescription>Upload your venue images</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">


                        <div className="card-content space-y-6">

                            {/* Sport Type Selection */}
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <Label htmlFor="court-sport">Select Sport *</Label>
                                    <Select value={selectedSportId} onValueChange={(id) => {
                                        console.log("id:", id)
                                        setSelectedSportId(id)
                                        let courts = VendorVenueDetailsById?.payload?.sports?.find(ele => ele._id === id).courts
                                        setAllCourtData(courts)
                                        setSelectedCourtId(null)
                                    }}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select sport" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {VendorVenueDetailsById?.payload?.sports?.map((sport, idx) => (
                                                <SelectItem key={idx} value={sport?._id}>{sport.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Thumbnail Image Upload */}
                            <div className={selectedSportId ? `opacity-100` : `opacity-25`}>
                                <Label htmlFor="thumbnail">Venue Thumbnail *</Label>
                                <div className={`flex flex-col items-center border-2 border-dashed border-flup-hairline-2 p-6 rounded-lg`}>
                                    {thumbnailPreview ? (
                                        <div className="relative">
                                            <img
                                                src={thumbnailPreview}
                                                alt="Thumbnail Preview"
                                                className="w-40 h-40 object-cover rounded-lg"
                                            />
                                            {updateturfImagesResponse.isLoading && isUploadingThumnail ? <Loader className="animate-spin absolute top-1/4 left-1/4  w-16 h-16 text-flup-info" />
                                                :
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteImage(-1)}
                                                        disabled={updateturfImagesResponse.isLoading}
                                                        className="absolute top-2 right-2 p-2 bg-flup-surface rounded-full shadow-md hover:bg-flup-surface-alt w-8 h-8"
                                                    >
                                                        <Trash2 className={`w-5 h-5 ${updateturfImagesResponse.isLoading ? " text-gray-200 " : " text-flup-ink-2 "}`} />
                                                    </button>


                                                    <button
                                                        type="button"
                                                        onClick={() => handleViewImage(thumbnailPreview)}
                                                        className="absolute bottom-2 right-2 p-2 bg-flup-info-soft0 rounded-full shadow-md hover:bg-blue-600 w-8 h-8"
                                                    >
                                                        <Eye className="w-4 h-4 text-white" />

                                                    </button>



                                                </>
                                            }
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center">
                                            <Camera className="w-12 h-12 text-flup-ink-3 mb-4" />
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleThumbnailUpload}
                                                className="hidden"
                                                id="thumbnail-upload"
                                            />
                                            <label
                                                htmlFor="thumbnail-upload"
                                                className="text-lg text-blue-800 cursor-pointer flex items-center space-x-4"
                                            >
                                                <UploadCloudIcon className='w-5 h-5 mr-2' /> Thumbnail
                                            </label>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Venue Court Image Uploads */}
                            <div className={selectedSportId ? `opacity-100` : `opacity-25`}>
                                <Label htmlFor="venue-images">Venue Court Images *</Label>


                                {/* Court Type Selection */}
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <Label htmlFor="court-sport">Select Court *</Label>
                                        <Select value={selectedCourtId || ""} onValueChange={(id) => {
                                            console.log("id:", id)
                                            setSelectedCourtId(id)
                                        }}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select Court" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {allCourtData?.length > 0 && allCourtData.map((court, idx) => (
                                                    <SelectItem key={idx} value={court?._id}>{court.courtName}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                               {selectedCourtId && <div className={selectedCourtId ? `opacity-100` : `opacity-25`}>

                                    <div className="grid grid-cols-2 gap-4">
                                        {venueImages.map((image, index) => (
                                            <div key={index} className="flex flex-col items-center border-2 border-dashed border-flup-hairline-2 p-6 rounded-lg">
                                                {image?.url ? (
                                                    <div className="relative">
                                                        <img
                                                            src={image.url}
                                                            alt={`Venue Preview ${index + 1}`}
                                                            className="w-40 h-40 object-cover rounded-lg"
                                                        />
                                                        {(updateturfImagesResponse.isLoading || deleteImagesResponse.isLoading) && currentCourtImageIndex === index ? <Loader className={`animate-spin absolute top-1/4 left-1/3  w-14 h-14  ${deleteImagesResponse.isLoading ? " text-red-600 " : " text-black-900 "} `} /> :

                                                            <button
                                                                disabled={updateturfImagesResponse.isLoading || !selectedCourtId}
                                                                type="button"
                                                                onClick={() => handleDeleteCourtImage(index, image)}
                                                                // onClick={() => handleDeleteImage(index)}

                                                                className="absolute top-2 right-2 p-2 bg-flup-surface rounded-full shadow-md hover:bg-flup-surface-alt"
                                                            >
                                                                <Trash2 className={`w-5 h-5 ${updateturfImagesResponse.isLoading ? " text-gray-200 " : " text-flup-ink-2 "}`} />
                                                            </button>
                                                        }
                                                    </div>
                                                ) : (
                                                    <div className={`${updateturfImagesResponse.isLoading ? 'opacity-25' : 'opacity-100 '} flex flex-col items-center`}>
                                                        <Camera className="w-12 h-12 text-flup-ink-3 mb-4" />
                                                        <input
                                                            disabled={(!selectedCourtId || !selectedSportId || updateturfImagesResponse.isLoading)}
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={(e) => handleVenueImageUpload(index, e)}
                                                            className="hidden"
                                                            id={`venue-upload-${index}`}
                                                        />
                                                        <label
                                                            htmlFor={`venue-upload-${index}`}
                                                            className="text-lg text-blue-800 cursor-pointer flex items-center space-x-4"
                                                        >
                                                            <UploadCloudIcon className='w-5 h-5 mr-2' /> Venue Image {index + 1}
                                                        </label>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>}
                            </div>
                            {/* Image View Modal */}
                            {viewingImage && (
                                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                                    <div className="relative bg-flup-surface p-4 rounded-lg shadow-lg">
                                        <img
                                            src={viewingImage}
                                            alt="Full View"
                                            className="w-80 h-80 object-contain rounded-lg"
                                        />
                                        <button
                                            onClick={() => setViewingImage(null)}
                                            className="absolute top-2 right-2 p-2 bg-flup-surface rounded-full shadow-md hover:bg-flup-surface-alt"
                                        >
                                            <X className="w-5 h-5 text-flup-ink-2" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-4 pt-4">
                                <Button variant="outline" onClick={onBack} className="flex-1">
                                    Back
                                </Button>
                                <ButtonWithLoader isLoading={VendorVenueDetailsById.isLoading} onClick={handleSubmit} className="flex-1 bg-flup-brand hover:bg-flup-brand-press text-white">
                                    Complete Setup
                                </ButtonWithLoader>
                            </div>
                        </div>

                    </CardContent>
                </Card>
            </div>
        </>



    );
};

export default SportsImageManagementForm;
